import { Capacitor } from '@capacitor/core';
import {
  LOG_LEVEL,
  Purchases,
  type CustomerInfo,
  type PurchasesOfferings,
  type PurchasesPackage,
} from '@revenuecat/purchases-capacitor';
import { RevenueCatUI, type PaywallResult } from '@revenuecat/purchases-capacitor-ui';
import type { ApiUser, BillingConfigResponse, BillingPlanId } from '@shared/types';

let configuredFor: string | null = null;
let listenerConfigured = false;
const customerInfoSubscribers = new Set<(customerInfo: CustomerInfo) => void>();

export function isNativeBillingPlatform() {
  return Capacitor.isNativePlatform() && ['ios', 'android'].includes(Capacitor.getPlatform());
}

export function revenueCatApiKey(config?: BillingConfigResponse | null) {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') return config?.revenueCat.iosApiKey ?? null;
  if (platform === 'android') return config?.revenueCat.androidApiKey ?? null;
  return null;
}

export async function configureRevenueCat(user: ApiUser, config: BillingConfigResponse) {
  if (!isNativeBillingPlatform()) return false;
  const apiKey = revenueCatApiKey(config);
  if (!apiKey) return false;
  const key = `${Capacitor.getPlatform()}:${apiKey}:${user.id}`;
  if (configuredFor === key) return true;

  await Purchases.setLogLevel({ level: import.meta.env.DEV ? LOG_LEVEL.DEBUG : LOG_LEVEL.WARN }).catch(() => {});
  await Purchases.configure({ apiKey, appUserID: user.id });
  await Purchases.setEmail({ email: user.email }).catch(() => {});
  await Purchases.setDisplayName({ displayName: user.displayName }).catch(() => {});
  configuredFor = key;
  await ensureCustomerInfoListener();
  return true;
}

export async function getNativeOfferings(): Promise<PurchasesOfferings> {
  return Purchases.getOfferings();
}

export function packageForPlan(
  offerings: PurchasesOfferings | null | undefined,
  planId: BillingPlanId,
): PurchasesPackage | null {
  const offering = offerings?.current;
  if (!offering) return null;
  if (planId === 'monthly') return offering.monthly ?? findPackage(offering.availablePackages, 'MONTHLY', ['pro_monthly', 'monthly']);
  return offering.annual ?? findPackage(offering.availablePackages, 'ANNUAL', ['pro_annual', 'yearly', 'annual']);
}

export async function purchaseNativePackage(pkg: PurchasesPackage): Promise<CustomerInfo> {
  const result = await Purchases.purchasePackage({ aPackage: pkg });
  return result.customerInfo;
}

export async function restoreNativePurchases(): Promise<CustomerInfo> {
  const result = await Purchases.restorePurchases();
  return result.customerInfo;
}

export async function getNativeCustomerInfo(): Promise<CustomerInfo> {
  const result = await Purchases.getCustomerInfo();
  return result.customerInfo;
}

export function hasActivePro(customerInfo: CustomerInfo | null | undefined, entitlementId = 'pro') {
  return Boolean(
    customerInfo?.entitlements.active[entitlementId]?.isActive ||
    customerInfo?.entitlements.active['squishy school Pro']?.isActive ||
    customerInfo?.entitlements.active.squishy_school_pro?.isActive,
  );
}

export async function presentNativePaywall(entitlementId = 'pro'): Promise<{ result: PaywallResult; customerInfo: CustomerInfo | null }> {
  const result = await RevenueCatUI.presentPaywallIfNeeded({
    requiredEntitlementIdentifier: entitlementId,
    displayCloseButton: true,
  });
  const customerInfo = await getNativeCustomerInfo().catch(() => null);
  return { result, customerInfo };
}

export async function presentNativeCustomerCenter() {
  await RevenueCatUI.presentCustomerCenter();
  return getNativeCustomerInfo().catch(() => null);
}

export function onNativeCustomerInfoUpdated(callback: (customerInfo: CustomerInfo) => void) {
  customerInfoSubscribers.add(callback);
  void ensureCustomerInfoListener();
  return () => {
    customerInfoSubscribers.delete(callback);
  };
}

async function ensureCustomerInfoListener() {
  if (listenerConfigured || !isNativeBillingPlatform()) return;
  listenerConfigured = true;
  await Purchases.addCustomerInfoUpdateListener((customerInfo) => {
    for (const subscriber of customerInfoSubscribers) subscriber(customerInfo);
  }).catch(() => {
    listenerConfigured = false;
  });
}

function findPackage(
  packages: PurchasesPackage[],
  packageType: 'MONTHLY' | 'ANNUAL',
  productIds: string[],
) {
  return packages.find((pkg) => pkg.packageType === packageType || productIds.includes(pkg.product.identifier)) ?? null;
}
